import { Component, OnInit } from '@angular/core';
import {
    ModuleAccountType,
    PartnerInformation,
    SoftwareModuleAccessConfiguration
} from '../../core-data-model';
import {
    CompanySettingsApi,
    PartnerInformationService
} from '../../core-services';
import { TranslationService } from '../../translations';

@Component({
    template: `
        <basic-page-layout>
            <ng-container page-header-title>
                <span data-testing="subscription-manager-page-header">
                    Billing Details
                </span>
            </ng-container>
            <ng-container page-main-body>
                @if (loading || !partnerInfo) {
                    <loading-dots></loading-dots>
                }
                @if (!loading && partnerInfo) {
                    <div>
                        <div class="detail-flex">
                            <dl>
                                <dd class="detail-title">
                                    {{
                                        TR.key(
                                            'billing-manager.billing-contact-title'
                                        )
                                    }}
                                </dd>
                                <dd class="detail-value">
                                    {{ billingContactName }}
                                </dd>
                                <dd
                                    class="open-peek-panel"
                                    (click)="
                                        toggleBillingContactDetailPeekPanel(
                                            true
                                        )
                                    "
                                >
                                    {{
                                        TR.key(
                                            'billing-manager.view-billing-contact'
                                        )
                                    }}
                                </dd>
                            </dl>
                            <billing-detail
                                class="billing-detail"
                                [detailTitle]="
                                    TR.key(
                                        'billing-manager.billing-frequency-title'
                                    )
                                "
                                [detailValue]="billingFrequency"
                            ></billing-detail>
                            <billing-detail
                                class="billing-detail"
                                [detailTitle]="
                                    TR.key(
                                        'billing-manager.billing-terms-title'
                                    )
                                "
                                [detailValue]="billingTerms"
                            ></billing-detail>
                        </div>
                        <br />
                        <div class="detail-flex">
                            <billing-detail
                                class="billing-detail"
                                [detailTitle]="
                                    TR.key(
                                        'billing-manager.contract-renewal-title'
                                    )
                                "
                                [detailValue]="contractEndDate"
                            ></billing-detail>
                            <billing-detail
                                class="billing-detail"
                                [detailTitle]="
                                    TR.key(
                                        'billing-manager.contract-start-title'
                                    )
                                "
                                [detailValue]="contractStartDate"
                            ></billing-detail>
                            <billing-detail
                                class="billing-detail"
                                [detailTitle]="
                                    TR.key(
                                        'billing-manager.contract-value-title'
                                    )
                                "
                                [detailValue]="contractValue"
                            ></billing-detail>
                        </div>
                        <br />
                        <billing-payment-details></billing-payment-details>
                    </div>
                }
            </ng-container>
        </basic-page-layout>
        @if (partnerInfo) {
            <peek-panel
                [open]="openBillingContactPanel"
                (close)="openBillingContactPanel = false"
            >
                <billing-contact-details
                    [partnerInfo]="partnerInfo"
                    (closePanel)="toggleBillingContactDetailPeekPanel($event)"
                ></billing-contact-details>
            </peek-panel>
        }
    `,
    styleUrls: ['./account-billing-manager.less'],
    standalone: false
})
export class AccountBillingManagerPageComponent implements OnInit {
    partnerInfo: PartnerInformation = null;
    openBillingContactPanel = false;
    loading = true;
    softwareModuleAccess: SoftwareModuleAccessConfiguration;

    constructor(
        private companySettingsApi: CompanySettingsApi,
        private partnerInfoService: PartnerInformationService,
        public TR: TranslationService
    ) {}

    get billingContactName(): string {
        return (
            this.getPartnerInfoMetadata('billingContactName') ??
            this.TR.key('billing-manager.details.no-contact')
        );
    }

    get billingFrequency(): string {
        const partnerInfoFreq = this.getPartnerInfoMetadata('billingFrequency');
        if (!partnerInfoFreq && this.isPaidAccount) {
            return this.TR.key('billing-manager.billing-detail.error');
        }
        return (
            partnerInfoFreq ??
            this.TR.key('billing-manager.details.no-frequency')
        );
    }

    get billingTerms(): string {
        if (this.getPartnerInfoMetadata('billingTerms')) {
            return this.getPartnerInfoMetadata('billingTerms');
        } else if (this.isPaidAccount) {
            return this.TR.key('billing-manager.billing-detail.error');
        } else {
            return this.TR.key('billing-manager.details.no-terms');
        }
    }

    get contractValue(): string {
        return (
            this.getPartnerInfoMetadata('billingContractValue') ??
            this.TR.key('billing-manager.details.no-terms')
        );
    }

    get contractStartDate(): string {
        const cs = this.getPartnerInfoMetadata('contractStartDate');
        if (!cs && this.isPaidAccount) {
            return this.TR.key('billing-manager.billing-detail.error');
        }
        return cs !== null
            ? new Date(cs).toLocaleDateString()
            : this.TR.key('billing-manager.details.no-contract');
    }

    get contractEndDate(): string {
        const ce = this.getPartnerInfoMetadata('contractEndDate');
        if (!ce && this.isPaidAccount) {
            return this.TR.key('billing-manager.billing-detail.error');
        }
        return ce !== null
            ? new Date(ce).toLocaleDateString()
            : this.TR.key('billing-manager.details.no-contract');
    }

    get isPaidAccount(): boolean {
        return (
            this.softwareModuleAccess.softwareModuleAccessConfig.findIndex(
                (module) => module.moduleAccountType === ModuleAccountType.Paid
            ) >= 0
        );
    }

    ngOnInit() {
        Promise.all([
            this.companySettingsApi.getCompanySoftwareModuleAccessConfiguration(),
            this.partnerInfoService.getPartnerInformationForCurrentCompany()
        ]).then((data) => {
            this.softwareModuleAccess = data[0];
            this.partnerInfo = data[1];
            this.loading = false;
        });
    }

    toggleBillingContactDetailPeekPanel(val: boolean) {
        this.openBillingContactPanel = val;
    }

    private getPartnerInfoMetadata(key: string) {
        const metadataObj = this.partnerInfo.accountMetadata.find(
            (m) => m.name === key
        );
        return metadataObj ? metadataObj.value : null;
    }
}
